import { Component, h, Prop, JSX, Listen, State, Host, Build, EventEmitter, Event } from '@stencil/core'
import trboService from '~/shared/trbo.service'

import { CartRead, CartService, AuthService, container } from '@rdss/client'
import WeinfreundeLogo from './assets/logo.svg'
import MenuData from './menu.data.json'
import { GlobalSearchService, WeinfreundeWishlistService } from '~/data-access'
import { FavoriteIcon } from '~/components/organism/wf-header/assets/icons'
import ehi from './assets/ehi-award.webp'
import { store } from '~/store'
import {
    getCategoryName, satelliteTrack,
    TrackingContext,
    TrackingDetails,
    TrackingDisturberType,
} from '@rdss/client/dist/catalog/types/tracking'

/**
 * MenuItem interface.
 * Represents a single menu item.
 *
 * @property {boolean} mobileOnly - Indicates if the menu item is only visible on mobile devices.
 * @property {boolean} desktopOnly - Indicates if the menu item is only visible on desktop devices.
 * @property {MenuItems} children - Represents the child menu items of a parent menu item.
 */
interface MenuItem extends Record<string, unknown> {
    mobileOnly?: boolean
    desktopOnly?: boolean
    children?: MenuItems
}

/**
 * MenuItems type.
 * Represents an array of MenuItem objects.
 */
type MenuItems = MenuItem[]

/**
 * WfHeader component.
 * This component composes all header-relevant elements.
 *
 * @example
 * <wf-header-2></wf-header-2>
 *
 * @part container - The main container of the header.
 * @part container-sticky - The main container when it's sticky.
 * @part mobile-menu - The mobile version of the menu.
 * @part logo - The logo of the website.
 * @part desktop-menu - The desktop version of the menu.
 * @part actions - The actions section of the header.
 * @part mobile-search-trigger - The trigger for the mobile search.
 * @part backdrop - The backdrop of the header.
 */
@Component({
    tag: 'wf-header-2', // rename if legacy wf-header (rdss-next) was removed
    styleUrl: 'wf-header.scss',
    scoped: true,
})
// eslint-disable-next-line require-jsdoc
export class WfHeader {
    /**
     * The desktop breakpoint.
     */
    @Prop() desktopBreakpoint = 1181

    /**
     * Displays a backdrop when menu are open if set to true.
     */
    @Prop() displayBackdrop = true

    /**
     * The snippets for the navigation.
     */
    @Prop() snippets = {
        cartButtonLabel: 'Zum Warenkorb',
        accountButtonLabel: 'Account anzeigen',
    }

    /**
     * The current category id of the page. Used to highlight the active category in the menu.
     */
    @Prop() categoryId?: number

    /**
     * The quantity of notes. Used to display the number of notes in the header.
     */
    @Prop() notesQuantity: number

    /**
     * The current search term. Used to prefill the search input field.
     */
    @Prop() searchTerm?: string

    /**
     * Display the header in minimalistic mode.
     */
    @Prop() displayMinimalistic?: boolean

    /**
     * The 'enableLoyalty' prop controls whether the loyalty feature is enabled.
     */
    @Prop() enableLoyalty = false

    @Prop() paybackServiceStatus: string

    @State() paybackActive = true

    /**
     * Event emitted once item has been added to cart.
     */
    @Event() itemAddedToCart: EventEmitter<{ cartLoaded: boolean }>

    /**
     * The menu data.
     *
     * @example
     *   [
     *    {
     *      "title": "Products",
     *      "url": "/products",
     *      "naviTrackingLink": "products",
     *      "children": [
     *        {
     *           "title": "Cars",
     *           "url": "/products/cars",
     *           "mobileOnly": true,
     *           "naviTrackingLink": "products-cars"
     *        }
     *      ]
     *    }
     *  ]
     */
    @State() menuData = MenuData

    /**
     * Holds the user state.
     */
    @State() userLoggedIn?: boolean

    /**
     * State to track if cart has new item.
     */
    @State() cartHasNewItem = false

    /**
     * State to track if viewport is desktop.
     */
    @State() isDesktopViewport = false

    /**
     * The card data state. Holds the current state of the shopping cart.
     */
    @State() private cartData: CartRead

    /**
     * State to track if the header is sticky. If true, it indicates that the header is currently in a sticky position.
     */
    @State() private isSticky = false

    /**
     * State to track if the cart summary is displayed. If true, it indicates that the cart summary is currently being displayed.
     */
    @State() private displayCartSummary = false

    /**
     * State to track if the wishlist animation is displayed.
     * If true, it indicates that the wishlist animation is currently being displayed.
     */
    @State() private displayWishlistAnimation = false

    /**
     * Event emitted when the trbo script has been loaded.
     */
    @Event() trboScriptLoaded: EventEmitter<void>

    /**
     * The cart service. Used to interact with the shopping cart.
     */
    private cartService = container.resolve(CartService)

    /**
     * The auth service.
     */
    private authService = container.resolve(AuthService)

    /**
     * The search service. Used to perform global searches.
     */
    private searchService = container.resolve(GlobalSearchService)

    /**
     * The wishlist service. Used to interact with the wishlist.
     */
    private wishlistService = container.resolve(WeinfreundeWishlistService)

    private animationDisplayTime = 2000

    private timer: number

    /**
     * @inheritdoc
     */
    async componentWillLoad() {
        await this.setUserLoggedInState()
        await this.loadCart()
        await this.loadWishlist()
        this.checkIsSticky()
        this.setViewportState()

        if (this.paybackServiceStatus === 'inactive') {
            this.paybackActive = false
        }

        this.menuData = this.menuData.map((item) => {
            if (!this.paybackActive && item.title === 'Payback') {
                return { ...item, desktopOnly: true }
            }
            return item
        })
    }

    /**
     * Event handler for 'addToCartTrackingListing' event.
     */
    @Listen('addToCartTrackingListingEnrich', { target: 'document' })
    async handleAddToCartTrackingListingEnrich(event: CustomEvent<{
        category,
        hasPseudo,
        name,
        orderNumber,
        price,
        quantity,
        context
    }>) {
        satelliteTrack({
            context: event.detail.context ??
                (event.detail.category === '3' ? TrackingContext.productListSearch : TrackingContext.productList),
            products: {
                [event.detail.orderNumber]: {
                    actionType: 'not_set',
                    category: getCategoryName(event.detail.category),
                    disturberType: event.detail.hasPseudo ? TrackingDisturberType.discount : TrackingDisturberType.none,
                    id: event.detail.orderNumber,
                    name: event.detail.name,
                    price: event.detail.price.toString(),
                    totalProductValue: (event.detail.price * event.detail.quantity).toString(),
                    units: event.detail.quantity.toString(),
                },
            },
        })
    }

    /**
     * Event handler for 'addToCartTrackingEnriched' event.
     */
    @Listen('addToCartTrackingEnriched', { target: 'document' })
    async handleAddToCartTrackingEnriched(event: CustomEvent<TrackingDetails>) {
        satelliteTrack(event.detail)
    }

    /**
     * Event handler for 'addToCartClicked' event.
     * This method is triggered when a new item is added to the cart.
     * It also refreshes the cart data by calling the loadCart method.
     */
    @Listen('addToCartClicked', { target: 'document' })
    async handleCartItemAdded(event?: CustomEvent<{ quantity?: number, orderNumber?: string }> ) {
        clearTimeout(this.timer)

        this.displayCartSummary = true

        store.state.cartIsLoading = true

        if (event?.detail) {
            const { orderNumber, quantity } = event.detail
            await this.cartService
                .addLineItem(orderNumber, quantity)
                .catch(e => console.debug(e.message))
                .finally(() => {
                    store.state.cartIsLoading = false
                    this.itemAddedToCart.emit({ cartLoaded: true })
                })
            trboService.trackAddToCart(orderNumber, quantity)
        }

        await this.loadCart()

        this.timer = window.setTimeout(() =>
            (this.displayCartSummary = false), this.animationDisplayTime)
    }

    /**
     * Event listener for 'addToWishlistClicked' event.
     */
    @Listen('wishlistItemAdded', { target: 'document' })
    handleWishlistItemAdded() {
        this.displayWishlistAnimation = true
        setTimeout(() => (this.displayWishlistAnimation = false), this.animationDisplayTime)
    }

    /**
     * Event handler for the 'userLoggedOut' event.
     * When the 'userLoggedOut' event is dispatched, it sets the 'userLoggedIn' state to false
     * and clears the wishlist.
     */
    @Listen('userLoggedOut', { target: 'document' })
    async handleUserLoggedOut() {
        this.userLoggedIn = false
        await this.loadWishlist(true)
    }

    /**
     * Event handler for the 'userLoggedIn' event.
     * When the 'userLoggedIn' event is dispatched, it sets the 'userLoggedIn' state to true
     * and reloads the wishlist.
     */
    @Listen('userLoggedIn', { target: 'document' })
    async handleUserLoggedIn() {
        this.userLoggedIn = true
        await this.loadWishlist(true)
    }

    /**
     * Event listener for 'resize' event.
     * It sets the 'isDesktopViewport' state based on the window inner width.
     */
    @Listen('resize', { target: 'window' })
    setViewportState() {
        this.isDesktopViewport = window.innerWidth >= this.desktopBreakpoint
    }

    /**
     * Event listener for 'scroll' event.
     * It sets the 'isSticky' state based on the window scroll Y position.
     */
    @Listen('scroll', { target: 'window' })
    checkIsSticky() {
        this.isSticky = window.scrollY > 0
    }

    /**
     * @inheritdoc
     */
    componentDidLoad() {
        if (Build.isBrowser) {
            trboService.init()
            if (this.cartData?.summary?.gross) {
                trboService.trackCurrentBasket(this.cartData.summary.gross.replace(',', '.'))
            }
            this.trboScriptLoaded.emit();
        }
    }

    /**
     * @inheritdoc
     */
    render(): JSX.Element {
        return (
            <Host>
                <header role="banner" class={{ 'is--desktop': this.isDesktopViewport }}>
                    <div class={{ 'header-container': true, 'is--sticky': this.isSticky }}
                        part={this.isSticky ? 'container-sticky' : 'container'}>
                        {this.displayMinimalistic ? this.renderMinimalisticHeader() : this.renderFullHeader()}
                    </div>

                    {this.displayBackdrop && <div class="backdrop"/>}
                </header>
            </Host>
        )
    }

    /**
     * Renders a minimalistic version of the header.
     */
    private renderMinimalisticHeader() {
        return (
            <div class="header-content minimalistic">
                <a href="/" class="logo">
                    <img width="200" height="37" src={WeinfreundeLogo} alt="Weinfreunde Logo"/>
                </a>
                <a class="ehi" target="_blank"
                    href="https://zertifikat.ehi-siegel.de/de/zertifikat/s/c61b38302369979b11eee0ea9be40353/zertifikat.html"
                    title="EHI Siegel">
                    <img width="56" height="56" alt="EHI Geprüfter Onlineshop" src={ehi}/>
                </a>
            </div>
        )
    }

    /**
     * Renders the full version of the header.
     */
    private renderFullHeader() {
        return (
            <div class="header-content">
                <vx-side-nav
                    categoryId={this.categoryId}
                    menuItems={this.mobileMenuItems}
                    desktopBreakpoint={this.desktopBreakpoint}/>

                <a href="/" class="logo">
                    <img width="200" height="37" src={WeinfreundeLogo} alt="Weinfreunde Logo"/>
                </a>

                <div class="desktop-menu">
                    <vx-mega-menu
                        categoryId={this.categoryId}
                        isActiveItemUnderlined={true}
                        menuItems={this.desktopMenuItems}/>
                </div>
                <div class="actions">
                    <vx-global-search
                        searchService={this.searchService}
                        searchTermMinLength={1}
                        searchTerm={this.searchTerm}
                        suggestionsTemplate="wf-global-search-suggestions"/>

                    <vx-drawer class="account-menu" backdrop>
                        <a slot="trigger" part="account-menu" href="/account"
                            aria-label={this.snippets.accountButtonLabel}>
                            <FavoriteIcon className={this.displayWishlistAnimation ? 'animate' : ''}/>
                            <vx-icon-button
                                aria-label={this.snippets.accountButtonLabel}
                                icon={this.userLoggedIn ? 'person_filled' : 'person'}/>
                        </a>
                        <wf-account-menu
                            formAutocomplete={true}
                            isUserLoggedIn={this.userLoggedIn}
                            enableLoyalty={this.enableLoyalty}
                            slot="content"/>
                    </vx-drawer>

                    <vx-drawer
                        part="cart-summary"
                        keepOpenOnOutsideClick={this.displayCartSummary}
                        open={this.displayCartSummary}>
                        <a slot="trigger" href="/checkout/cart" aria-label={this.snippets.cartButtonLabel}>
                            {this.cartData?.cartItemsCount > 0 && (
                                <vx-counter-badge quantity={this.cartData?.cartItemsCount}/>)}
                            <vx-icon-button
                                aria-label={this.snippets.cartButtonLabel}
                                icon={this.cartData?.cartItemsCount > 0 ? 'shopping_cart_filled' : 'shopping_cart'}/>
                        </a>
                        <vx-cart-summary
                            slot="content"
                            displayTime={this.animationDisplayTime}
                            cartData={this.cartData}/>
                    </vx-drawer>
                </div>
            </div>
        )
    }

    /**
     * Returns the mobile menu items by excluding items with 'desktopOnly' property.
     */
    private get mobileMenuItems() {
        return this.excludeItemsByProperty('desktopOnly')
    }

    /**
     * Returns the desktop menu items by excluding items with 'mobileOnly' property.
     */
    private get desktopMenuItems() {
        return this.excludeItemsByProperty('mobileOnly')
    }

    /**
     * Excludes items by property from the menu data.
     *
     * @param {string} name - The property name to exclude items by.
     * @param {MenuItems} items - The menu items to exclude from. Defaults to this.menuData.
     */
    private excludeItemsByProperty = (name: string, items: MenuItems = this.menuData): never => items
        .filter(item => !item[name] === true)
        .map((item) => {
            if (item.children) {
                return { ...item, children: this.excludeItemsByProperty(name, item.children) }
            } else {
                return item;
            }
        }) as never

    /**
     * Gets the cart data from the cart service.
     */
    private async loadCart() {
        if(Build.isBrowser) {
            const { data } = await this.cartService.getCart()
            this.cartData = data
        }
    }

    /**
     * Loads the user wishlist.
     */
    private async loadWishlist(forceClear?: boolean) {
        if (forceClear) await this.wishlistService.clearWishlist()
        if (Build.isBrowser) await this.wishlistService.getNotes()
    }

    /**
     * Sets the 'userLoggedIn' state based on the user's authentication status.
     * If the build is server-side, it directly checks the authentication status.
     * Additionally, it adds an event listener to the 'pageshow' event to update the 'userLoggedIn' state
     * when the page is shown.
     *
     * @returns {Promise<void>} A Promise that resolves when the 'userLoggedIn' state is set.
     */
    private async setUserLoggedInState(): Promise<void> {
        if(Build.isBrowser) {
            this.userLoggedIn = await this.authService.isAuthenticated()
        }
    }
}
